import {
  put, takeLatest, takeEvery, select, all, take,
} from 'redux-saga/effects';
import _ from 'lodash';
import localStorage from 'local-storage';
import jwtDecode from 'jwt-decode';
import { TeammoApi } from 'app/utils/redux/helpers';
import history from 'app/routes/history';
import {
  CognitoSignUp,
  CognitoConfirmEmail,
  CognitoResendVerificationCode,
  CognitoLogin,
} from 'app/utils/aws/awsCognito';
import {
  OnBoardingActionTypes,
  OnBoardingActionTypes as types,
} from 'app/ducks/onboarding/types';
import { parseCognitoErrorMessage } from 'app/ducks/onboarding/selectors';
import { AuthenticationActionTypes } from 'app/ducks/authentication/types';

import {
  sagaActions,
  updateUserData,
  loginUser,
  createUserAndTeam,
} from 'app/ducks/onboarding/actions';

import { selectLastTeam, getTeam } from 'app/ducks/teams/actions';
import { sagaActions as authSagaActions } from 'app/ducks/authentication/actions';

import { getClub } from 'app/ducks/clubs/actions';

// const window: any;

function* createNewUserSaga(action): any {
  try {
    yield put(sagaActions.operationPending());
    const [firstName, lastName] = action.payload.fullName.split(' ');
    const newUser = yield select((state) => state.onBoarding.inFlightUser);

    const userInfo = {
      email: action.payload.email,
      password: action.payload.password,
      firstName,
      lastName,
      fullName: action.payload.fullName,
      mobile: action.payload.mobile,
    };

    const clientMetadata = {
      clubName: newUser.team.clubName,
      teamName: newUser.team.teamName,
      teamSport: newUser.team.selectedSport,
      userType: newUser.userType,
    };

    const cog = yield CognitoSignUp(userInfo, clientMetadata);
    if (!_.isEmpty(cog?.result?.username)) {
      localStorage.set('onboarding', { email: cog.result.username });
    }

    yield put(yield updateUserData(userInfo));
    yield put(sagaActions.userCreationSuccess());

    history.push({
      pathname: '/get-started/verify',
      data: {
        email: action.payload.email,
      },
    });
  } catch (error) {
    if (error.code === 'UsernameExistsException') {
      try {
        yield CognitoResendVerificationCode(action.payload.email);
        yield put(sagaActions.userCreationSuccess());

        history.push({
          pathname: '/get-started/verify',
          data: {
            email: action.payload.email,
          },
        });
      } catch (e) {
        yield put(
          sagaActions.userCreationFailed(
            parseCognitoErrorMessage(e.code, e.message),
          ),
        );
      }
    } else {
      yield put(sagaActions.userCreationFailed(error.message));
    }
  }
}

function* createUserAndTeamSaga(action): any {
  yield put(sagaActions.operationPending());
  const userId = jwtDecode(action.payload.jwtToken).sub;
  const [, user] = yield (yield TeammoApi(yield select())).users.create({
    id: userId,
    email: action.payload.email,
    password: action.payload.password,
    mobile: action.payload.mobile,
    firstName: action.payload.firstName,
    lastName: action.payload.lastName,
    userType: action.payload.userType,
  });

  const intercomPayload = {
    app_id: INTERCOM_APP_ID,
    user_id: userId,
    email: action.payload.email,
    name: `${action.payload.firstName} ${action.payload.lastName}`,
  };

  if ({}.toString.call(window.Intercom) === '[object Function]') {
    window.Intercom('boot', intercomPayload);
  }

  if (action.payload.team) {
    if (action.payload.userType === 'team') {
      const [, createdTeam] = yield (yield TeammoApi(
        yield select(),
      )).teams.create({
        name: action.payload.team.teamName,
        sport: action.payload.team.selectedSport,
        organisation: {
          id: action.payload.team.organisationId,
        },
      });

      if (createdTeam) {
        yield put(sagaActions.userCreateTeamSuccess(createdTeam.data));
        yield put(yield getTeam(createdTeam.data.id));
      }
    } else if (action.payload.userType === 'club') {
      const [, createdClub] = yield (yield TeammoApi(
        yield select(),
      )).clubs.create({
        name: action.payload.team.clubName,
        sport: action.payload.team.selectedSport,
        organisation: {
          id: action.payload.team.organisationId,
        },
      });

      if (createdClub) {
        yield put(sagaActions.userCreateClubSuccess(createdClub.data));
        yield put(yield getClub(createdClub.data.id));
      }
    }
  }
  if (user) {
    yield put(sagaActions.userCreationSuccess());
  }
}

function* loginUserSaga(action): any {
  yield put(sagaActions.operationPending());
  yield put(authSagaActions.authenticateCognitoUserPending());
  const storedEmail = !_.isEmpty(localStorage.get('onboarding'))
    ? localStorage.get('onboarding').email
    : '';
  const email = action.payload.email.toLowerCase() || storedEmail.toLowerCase();
  const { password } = action.payload;
  if (!_.isEmpty(email) && !_.isEmpty(password)) {
    try {
      const response = yield CognitoLogin({
        email,
        password: action.payload.password,
      });
      const { jwtToken } = response.signInUserSession.idToken;
      yield put(
        authSagaActions.authenticateCognitoUserSuccess(
          response.signInUserSession,
        ),
      );
      // if (ENVIRONMENT === 'development') {
      //   yield put(
      //     createUserAndTeam({
      //       ...action.payload,
      //       jwtToken,
      //     }),
      //   );
      // }
      yield put(selectLastTeam());
    } catch (error) {
      yield put(authSagaActions.authenticateCognitoUserFailed(error.message));
    }
  } else {
    history.push('/login');
  }
}

function* verifyUserSaga(action): any {
  const storedEmail = !_.isEmpty(localStorage.get('onboarding'))
    ? localStorage.get('onboarding').email
    : '';
  const email = action.payload.email.toLowerCase() || storedEmail.toLowerCase();
  try {
    yield put(sagaActions.operationPending());
    const newUser = yield select((state) => state.onBoarding.inFlightUser);

    const clientMetadata = {
      clubName: newUser.team.clubName,
      teamName: newUser.team.teamName,
      teamSport: newUser.team.selectedSport,
      userType: newUser.userType,
    };
    const response = yield CognitoConfirmEmail(
      email,
      action.payload.value,
      clientMetadata,
    );
    const inflightUser = yield select((state) => state.onBoarding.inFlightUser);
    yield put(loginUser(inflightUser));
    yield take(AuthenticationActionTypes.AUTHENTICATE_COGNITO_USER_SUCCESS);
    // yield take(types.USER_CREATION_SUCCESS);
    if (inflightUser.userType === 'team') {
      history.push('/get-started/connect');
    } else {
      history.push('/');
    }
    yield put(sagaActions.verificationSuccess(response.user));
  } catch (error) {
    yield put(sagaActions.verificationFailed(error.message));
  }
}

function* createBankAccountSaga(action): any {
  yield put(sagaActions.operationPending());
  const [error, bank] = yield (yield TeammoApi(
    yield select(),
  )).bankAccounts.create({
    accountHolderName: action.payload.name,
    accountNumber: action.payload.number,
    routingNumber: action.payload.bsb,
    country: {
      id: 'AUS',
    },
    bankName: action.payload.bank,
  });

  if (error) {
    yield put(sagaActions.bankAccountCreationFailed(error.message));
  } else if (bank) {
    yield put(sagaActions.operationFinished());
    yield put(sagaActions.continueToDashboard());
    history.push('/dashboard');
  }
}

/* ********************************* SAGA WATCHERS ********************************* */

function* createNewUserSagaWatcher(): any {
  yield takeEvery(types.CREATE_NEW_USER_REQUESTED, createNewUserSaga);
}

function* createUserAndTeamSagaWatcher(): any {
  yield takeEvery(types.CREATE_USER_AND_TEAM_REQUESTED, createUserAndTeamSaga);
}

function* loginUserSagaWatcher(): any {
  yield takeEvery(types.LOGIN_USER_REQUESTED, loginUserSaga);
}

function* verifyUserSagaWatcher(): any {
  yield takeEvery(types.VERIFY_USER_REQUESTED, verifyUserSaga);
}

function* createBankAccountSagaWatcher(): any {
  yield takeLatest(types.CREATE_BANK_ACCOUNT_REQUESTED, createBankAccountSaga);
}

export default function* root(): any {
  yield all([
    createNewUserSagaWatcher(),
    createUserAndTeamSagaWatcher(),
    loginUserSagaWatcher(),
    verifyUserSagaWatcher(),
    createBankAccountSagaWatcher(),
  ]);
}
